import { useEffect, useMemo, useState } from "react";
import useAxios from "axios-hooks";

import { useErrorAlert, useSuccessAlert, useUserRole } from "hooks";
import { AxiosRequestConfig } from "axios";

import API from "constants/api";

import { Country, FilterOptions } from "types/global";
import { BUYER, LAB, SUPPLIER } from "constants/global";
import { EFillingReportType } from "../pages/EFillingReport";

function useFetchEFilling<T>({
  method = "GET",
  api = API.SKU,
  id = "",
  url = "",
  successAlertEnabled = false,
  successMessage = "updated successfully.",
}: {
  method?: AxiosRequestConfig["method"];
  api?: API;
  id?: string;
  url?: string;
  successAlertEnabled?: boolean;
  successMessage?: string;
}) {
  const { setError, setAppError } = useErrorAlert();
  const { setSuccess } = useSuccessAlert();

  const { isBuyer, isSupplier, isLab } = useUserRole();

  const [data, setData] = useState<T>();

  const PATH = isBuyer ? `/${BUYER}${api}` : isSupplier ? `/${SUPPLIER}${api}` : `/${LAB}${api}`;

  const [{ data: fetchData, loading: isLoading, error }, refetch] = useAxios(
    {
      url: !id && !url ? PATH : `${PATH}/${id}${url}`,
      method,
    },
    {
      manual: true,
    },
  );

  useEffect(() => {
    if (error) setError(error);
  }, [error]);

  useEffect(() => {
    if (fetchData?.code !== 200) {
      setAppError({
        code: fetchData?.code,
        message: fetchData?.message,
      });
    }

    if (successAlertEnabled && fetchData?.code === 200) setSuccess(successMessage);

    if (fetchData?.data) setData(fetchData?.data);
  }, [fetchData]);

  return useMemo(
    () => ({
      data: data as T,
      isLoading,
      refetch,
      error,
    }),
    [id, url, data, isLoading],
  );
}

function useUpdateEFilling<Req, Res = { id?: string }>({
  method = "POST",
  api = API.SKU,
  id = "",
  url = "",
  successAlertEnabled = false,
  successMessage = "Submitted successfully.",
}: {
  method?: AxiosRequestConfig["method"];
  api?: API;
  id?: string;
  url?: string;
  successAlertEnabled?: boolean;
  successMessage?: string;
}) {
  const { setError, setAppError } = useErrorAlert();
  const { setSuccess } = useSuccessAlert();

  const { isBuyer, isSupplier } = useUserRole();

  const [data, setData] = useState<Res>();

  const PATH = isBuyer ? `/${BUYER}${api}` : isSupplier ? `/${SUPPLIER}${api}` : `/${LAB}${api}`;

  const [{ data: fetchData, loading: isLoading, error }, refetch] = useAxios(
    {
      url: !url ? PATH : !id ? `${PATH}/${url}` : `${PATH}/${id}${url}`,
      method,
    },
    {
      manual: true,
    },
  );

  useEffect(() => {
    if (error) setError(error);
  }, [error]);

  useEffect(() => {
    if (fetchData?.code !== 200) {
      setAppError({
        code: fetchData?.code,
        message: fetchData?.message,
      });
    }

    if (successAlertEnabled && fetchData?.code === 200) setSuccess(successMessage);

    if (fetchData?.data) setData(fetchData?.data);
  }, [fetchData]);

  return useMemo(
    () => ({
      data,
      isLoading,
      refetch: (payload: Req) => refetch({ data: payload }),
      error,
    }),
    [api, id, url, successAlertEnabled, successMessage, data, isLoading],
  );
}

export function useEFillingLog(skuId: string) {
  const { setError } = useErrorAlert();

  const commonPath = `/${skuId}/e_filing/logs`;

  const PATH = `/${BUYER}${API.SKU}${commonPath}`;

  const [{ loading: isLoading, error }, refetch] = useAxios(
    {
      url: `${PATH}`,
      method: "POST",
    },
    {
      manual: true,
    },
  );

  useEffect(() => {
    if (error) setError(error);
  }, [error]);

  return useMemo(
    () => ({
      isLoading,
      refetch: (payload: FilterOptions) => refetch({ data: payload }),
      error,
    }),
    [isLoading],
  );
}

// export function useEffilingLog(skuId: string, taskId: string) {
//   return useUpdateEFilling<{ selectedTaskIds: number[] }>({
//     id: skuId,
//     url: `/tasks/${taskId}/e_filing/logs`,
//   });
// }

export function useSubmitEFillingReport(skuId: string, taskId: string) {
  return useUpdateEFilling<{ selectedTaskIds: number[] }>({
    id: skuId,
    url: `/tasks/${taskId}/e_filling/import`,
  });
}

export function useGetEFillingReport<T = EFillingReportType>(skuId: string, taskId: string) {
  return useFetchEFilling<T>({
    id: skuId,
    url: `/tasks/${taskId}/submit_e_filing`,
  });
}

export function checkIsEFilling(exportMarket: Country) {
  return false;
  // return [Country.GLB, Country.US].includes(exportMarket);
}
